import api from './client'

// ── types ──────────────────────────────────────────────────────────────────

/** What a scheduler's chain runs. Mirrors `schedule_service.STEP_KINDS`. */
export type StepKind =
  | 'process' | 'report' | 'visual' | 'dashboard'
  | 'action' | 'agent' | 'function' | 'system' | 'playbook'

export type CadenceType = 'cron' | 'interval' | 'daily' | 'weekly' | 'monthly'

/** Where a step's artefact goes. Every destination is independent — an
 *  artefact that was filed but not emailed is a partial success, recorded. */
export interface DeliverSpec {
  dms_repo?: string
  dms_folder?: string
  filename?: string
  email?: EmailSpec
}

export interface EmailSpec {
  /** Literal addresses — an outside party. */
  to?: string[]
  /** Project users, by id. */
  users?: string[]
  /** Roles, resolved at SEND time, so joining Finance starts the pack. */
  roles?: string[]
  subject?: string
  body?: string
}

export interface ScheduleStep {
  kind: StepKind
  obj_name?: string
  args?: Record<string, unknown>
  /** Per-kind extras, passed through untouched. */
  format?: string
  persist?: boolean
  action_name?: string
  criteria?: Record<string, unknown>
  limit?: number
  function?: string
  task?: string
  /** A `playbook` step: the persona that runs it. */
  persona?: string
  deliver?: DeliverSpec
}

export interface Cadence {
  type: CadenceType
  timezone?: string
  time?: string
  days?: number[]
  day?: number | 'last'
  cron_expression?: string
  interval_value?: number
  interval_unit?: 'minutes' | 'hours' | 'days'
}

export interface ScheduleModel {
  name: string
  title?: string
  description?: string
  target: ScheduleStep
  then?: ScheduleStep[]
  schedule: Cadence
  run_as?: string
  enabled?: boolean
  on_failure?: { stop?: boolean; raise_work_item?: boolean; notify_roles?: string[] }
  ioflow?: { flow_id?: string | null; synced_fingerprint?: string | null }
}

/**
 * What IOFlow will actually do, when that differs from what was authored.
 * IOFlow stores `schedule.timezone` and builds its cron trigger without it,
 * so a screen that showed only the authored time would be showing a moment
 * that never happens.
 */
export interface TimezoneDrift {
  authored_timezone: string
  server_timezone: string
  minutes: number
  message: string
}

export interface ScheduleView extends ScheduleModel {
  cadence_text: string
  next_run: string | null
  timezone_drift: TimezoneDrift | null
  step_count: number
  last_run?: JobRun | null
}

export type RunStatus =
  | 'queued' | 'running' | 'succeeded' | 'failed' | 'skipped' | 'cancelled'

export interface JobRun {
  run_id: string
  parent_run_id: string | null
  project: string
  schedule_name: string | null
  step_no: number
  kind: string | null
  obj_name: string | null
  args: Record<string, unknown> | null
  trigger_kind: string
  actor: string | null
  status: RunStatus
  queued_at: string | null
  started_at: string | null
  finished_at: string | null
  output_ref: { filename?: string; bytes?: number; mime?: string;
                repo?: string; folder?: string; document_id?: string } | null
  result_summary: string | null
  error_text: string | null
  ioflow_execution_id: string | null
  duration_seconds: number | null
  steps?: JobRun[]
}

/** One row of the DERIVED back-reference an object's designer tab shows. */
export interface ObjectSchedule {
  name: string
  title: string
  enabled: boolean
  step_no: number
  step_count: number
  kind: StepKind
  schedule: Cadence
  next_run: string | null
}

// ── authoring ──────────────────────────────────────────────────────────────

export async function listSchedules(project: string) {
  const res = await api.post('/schedules/list', { project })
  return res.data as { success: boolean; schedules: ScheduleView[] }
}

export async function getSchedule(project: string, name: string) {
  const res = await api.post('/schedules/get', { project, name })
  return res.data as {
    success: boolean; schedule: ScheduleView; next_runs: string[]
  }
}

export async function saveSchedule(
  project: string, model: ScheduleModel, originalName?: string,
) {
  const res = await api.post('/schedules/save', {
    project, model, original_name: originalName || null,
  })
  return res.data as {
    success: boolean; schedule: ScheduleView; next_runs: string[]
  }
}

export async function validateSchedule(project: string, model: ScheduleModel) {
  const res = await api.post('/schedules/validate', { project, model })
  return res.data as {
    success: boolean; errors?: string[]
    schedule?: ScheduleView; next_runs?: string[]
  }
}

/** Next fire times for a cadence that has not been saved yet. */
export async function previewCadence(schedule: Cadence, count = 5) {
  const res = await api.post('/schedules/preview-cadence', { schedule, count })
  return res.data as {
    success: boolean; errors?: string[]
    schedule?: Cadence; cadence_text?: string
    next_runs?: string[]; timezone_drift?: TimezoneDrift | null
  }
}

export async function deleteSchedule(project: string, name: string) {
  const res = await api.post('/schedules/delete', { project, name })
  return res.data as { success: boolean }
}

export async function setScheduleEnabled(
  project: string, name: string, enabled: boolean,
) {
  const res = await api.post('/schedules/set-enabled', { project, name },
                             { params: { enabled } })
  return res.data as { success: boolean; schedule: ScheduleView }
}

/**
 * The schedulers that run one object — DERIVED from the schedule files, never
 * stored on the object, which is why this tab and the Schedulers screen can
 * never disagree.
 */
export async function schedulesForObject(project: string, objName: string) {
  const res = await api.post('/schedules/for-object', { project },
                             { params: { obj_name: objName } })
  return res.data as { success: boolean; schedules: ObjectSchedule[] }
}

// ── running ────────────────────────────────────────────────────────────────

export async function runNow(project: string, name: string) {
  const res = await api.post('/schedules/run-now', { project, name })
  return res.data as { success: boolean; run_id: string; status: string }
}

export async function listRuns(project: string, opts: {
  name?: string; objName?: string; status?: string; trigger?: string
  limit?: number; offset?: number
} = {}) {
  const res = await api.post('/schedules/runs', {
    project,
    name: opts.name || null,
    obj_name: opts.objName || null,
    status: opts.status || null,
    trigger: opts.trigger || null,
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
  })
  return res.data as { success: boolean; runs: JobRun[] }
}

export async function getRun(project: string, runId: string) {
  const res = await api.post('/schedules/run', { project },
                             { params: { run_id: runId } })
  return res.data as { success: boolean; run: JobRun }
}

/** Download URL for one artefact a run produced. */
export function runOutputUrl(project: string, runId: string, filename: string) {
  const q = new URLSearchParams({ project, run_id: runId, filename })
  return `/api/schedules/run-output?${q.toString()}`
}
